import React, { useState, useRef, useEffect } from 'react'

import { Animate } from '@/components/Animate'
import { Input } from '@/components/Input'
import { ChevronIcon } from '@/icons'
import useClickOutside from '@/hooks/useClickOutside'

import s from './MultiSelect.module.css'

type Item = {
  label: string
  value: string
}

type Props = {
  items: Item[]
  placeholder?: string
  openPosition?: 'top' | 'bottom'
  onChange?: (selectedItems: Item[]) => void
  selectedTextFn?: (items: Item[]) => string
}

export const MultiSelect = ({
  items,
  placeholder = 'Select',
  openPosition = 'bottom',
  onChange,
  selectedTextFn,
}: Props) => {
  const [isOpen, setIsOpen] = useState(false)
  const [selectedItems, setSelectedItems] = useState<Item[]>([])
  const [inputValue, setInputValue] = useState('')

  const inputRef = useRef<HTMLInputElement>(null)
  const containerRef = useClickOutside<HTMLDivElement>(() => {
    if (isOpen) {
      setIsOpen(false)
    }
  })
  const firstItemRef = useRef<HTMLLIElement>(null)
  const lastItemRef = useRef<HTMLLIElement>(null)

  const filteredItems = items.filter((item) =>
    item.label.toLowerCase().includes(inputValue.toLowerCase())
  )

  const isItemSelected = (item: Item) =>
    selectedItems.some((selectedItem) => selectedItem.value === item.value)

  const toggleItem = (item: Item) => {
    setSelectedItems((currentItems) => {
      const exists = currentItems.some((i) => i.value === item.value)
      if (exists) {
        return currentItems.filter((i) => i.value !== item.value)
      } else {
        return [...currentItems, item]
      }
    })
  }

  const toggleAll = () => {
    if (selectedItems.length === items.length) {
      setSelectedItems([])
    } else {
      setSelectedItems([...items])
    }
  }

  const getSelectedItemsText = () => {
    return !items.length
      ? placeholder
      : selectedTextFn
      ? selectedTextFn(selectedItems)
      : selectedItems.length
      ? selectedItems.map((item) => item.label).join(', ')
      : placeholder
  }

  useEffect(() => {
    const handleTabKey = (e: KeyboardEvent) => {
      if (e.key === 'Tab') {
        // If shift + tab on first element, move to last element
        if (e.shiftKey && document.activeElement === firstItemRef.current) {
          e.preventDefault()
          lastItemRef.current?.focus()
        }
        // If tab on last element, move to first element
        else if (
          !e.shiftKey &&
          document.activeElement === lastItemRef.current
        ) {
          e.preventDefault()
          firstItemRef.current?.focus()
        }
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleTabKey)
    }

    return () => {
      document.removeEventListener('keydown', handleTabKey)
    }
  }, [isOpen])

  // useEffect(() => {
  //   if (isOpen && inputRef.current) {
  //     inputRef.current.focus()
  //   }
  // }, [isOpen])

  useEffect(() => {
    onChange?.(selectedItems)
  }, [selectedItems])

  const isIndeterminate =
    selectedItems.length > 0 && selectedItems.length < items.length

  const handleInputKeyDown = (event: React.KeyboardEvent) => {
    // Make sure Tab doesn't move focus outside the dropdown
    if (event.key === 'Tab' && !event.shiftKey) {
      event.preventDefault()
      firstItemRef.current?.focus()
    }
  }

  return (
    <div ref={containerRef} className={s.container}>
      <button
        className={s.trigger}
        onClick={() => setIsOpen(!isOpen)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            setIsOpen(!isOpen)
          }
        }}
        type="button"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        disabled={!items.length}
      >
        <div className={s.triggerText}>{getSelectedItemsText()}</div>
        <div aria-hidden="true">{ChevronIcon}</div>
      </button>

      <Animate
        in={isOpen}
        className={s.transition}
        classNameOut={s.transitionExit}
      >
        <div className={`${s.dropdown} ${s[openPosition]}`}>
          {items.length > 5 && (
            <div className={s.search}>
              <Input
                ref={inputRef}
                name="search"
                autoComplete="off"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleInputKeyDown}
                placeholder="Search..."
                fullWidth
                autoFocus
              />
            </div>
          )}

          <ul role="listbox" aria-multiselectable="true" className={s.list}>
            {items.length > 5 && (
              <li
                ref={firstItemRef}
                onClick={toggleAll}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    toggleAll()
                  } else if (e.key === 'Tab' && e.shiftKey) {
                    e.preventDefault()
                    inputRef.current?.focus()
                  }
                }}
                tabIndex={0}
                role="option"
                aria-selected={selectedItems.length === items.length}
                className={s.item}
              >
                <input
                  type="checkbox"
                  ref={(el) => {
                    if (el) {
                      el.indeterminate = isIndeterminate
                    }
                  }}
                  tabIndex={-1}
                  checked={selectedItems.length === items.length}
                  onChange={(e) => {
                    // This prevents double triggering
                    e.stopPropagation()
                    toggleAll()
                  }}
                  onClick={(e) => e.stopPropagation()}
                  aria-label="Select all"
                  className={s.checkbox}
                />
                <b>Select All</b>
              </li>
            )}

            {filteredItems.map((item, index) => {
              const isLast = index === filteredItems.length - 1

              return (
                <li
                  key={item.value}
                  ref={isLast ? lastItemRef : undefined}
                  onClick={() => toggleItem(item)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      toggleItem(item)
                    } else if (e.key === 'Tab' && !e.shiftKey && isLast) {
                      e.preventDefault()
                      inputRef.current?.focus()
                    }
                  }}
                  tabIndex={0}
                  role="option"
                  aria-selected={isItemSelected(item)}
                  className={s.item}
                >
                  <input
                    type="checkbox"
                    checked={isItemSelected(item)}
                    tabIndex={-1}
                    onChange={(e) => {
                      // This prevents double triggering
                      e.stopPropagation()
                      toggleItem(item)
                    }}
                    onClick={(e) => e.stopPropagation()}
                    aria-label={`Select ${item.label}`}
                    className={s.checkbox}
                  />
                  {item.label}
                </li>
              )
            })}
          </ul>
        </div>
      </Animate>
    </div>
  )
}
